import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search, Filter, Mail, Phone, ShoppingBag, Calendar,
  ChevronLeft, ChevronRight, ArrowUpDown, User, Eye
} from 'lucide-react';

const AdminCustomers = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const customers = [
    { id: 1, name: 'Subashini Vasudevan', email: 'subashini@email.com', phone: '+44 7700 123456', orders: 12, totalSpent: 456.32, joined: '2023-01-15', status: 'active', lastOrder: '2026-06-08' },
    { id: 2, name: 'Moorthi Rajamanickam', email: 'moorthi@email.com', phone: '+44 7700 234567', orders: 8, totalSpent: 234.99, joined: '2023-03-22', status: 'active', lastOrder: '2026-06-08' },
    { id: 3, name: 'Divakar Hariraju', email: 'divakar@email.com', phone: '+44 7700 345678', orders: 15, totalSpent: 678.45, joined: '2022-11-05', status: 'active', lastOrder: '2026-06-07' },
    { id: 4, name: 'Keerthivasan R', email: 'keerthi@email.com', phone: '+44 7700 456789', orders: 5, totalSpent: 123.50, joined: '2024-02-10', status: 'active', lastOrder: '2026-06-07' },
    { id: 5, name: 'Subiksha R', email: 'subiksha@email.com', phone: '+44 7700 567890', orders: 20, totalSpent: 890.75, joined: '2022-08-18', status: 'vip', lastOrder: '2026-06-06' },
    { id: 6, name: 'Priya Kumar', email: 'priya@email.com', phone: '+44 7700 678901', orders: 3, totalSpent: 67.20, joined: '2024-05-01', status: 'new', lastOrder: '2026-06-06' },
  ];

  const filteredCustomers = customers.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          c.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'vip': return 'bg-purple-100 text-purple-700';
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'inactive': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-500 mt-1">Manage customer accounts and view purchase history</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-white px-4 py-2 rounded-xl border border-gray-100 shadow-sm">
            <span className="text-sm text-gray-500">Total: </span>
            <span className="font-bold text-gray-900">{customers.length}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 text-sm"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'vip', 'new'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-all ${
                statusFilter === status
                  ? 'bg-primary-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button className="flex items-center gap-1 hover:text-gray-700">
                    Orders <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button className="flex items-center gap-1 hover:text-gray-700">
                    Total Spent <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredCustomers.map((customer, index) => (
                <motion.tr
                  key={customer.id}
                  className="hover:bg-gray-50 transition-colors"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {customer.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{customer.name}</p>
                        <p className="text-xs text-gray-500">ID: #{customer.id.toString().padStart(4, '0')}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-600 flex items-center gap-1.5">
                        <Mail size={12} className="text-gray-400" /> {customer.email}
                      </p>
                      <p className="text-sm text-gray-600 flex items-center gap-1.5">
                        <Phone size={12} className="text-gray-400" /> {customer.phone}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <ShoppingBag size={16} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{customer.orders}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-bold text-gray-900">£{customer.totalSpent.toFixed(2)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${getStatusBadge(customer.status)}`}>
                      {customer.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Calendar size={14} className="text-gray-400" />
                      {new Date(customer.joined).toLocaleDateString('en-GB')}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                      <Eye size={16} />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <p className="text-sm text-gray-500">Showing 1 to {filteredCustomers.length} of {filteredCustomers.length} customers</p>
          <div className="flex items-center gap-2">
            <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-400">
              <ChevronLeft size={16} />
            </button>
            <button className="px-3 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium">1</button>
            <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-400">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomers;
